import PropTypes from 'prop-types';
import React, { useContext, useCallback, useState, useEffect, useMemo } from 'react';
import _ from 'underscore';
import {
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogTrigger, Button, DialogContent, DialogActions,
  Image, makeStyles, mergeClasses, shorthands, Badge, Checkbox, tokens, Spinner,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { useCopilotLoggers } from '@bingads-webui-campaign-react/copilot-loggers';

import { placements } from '../model/constants';

const useStyles = makeStyles({
  selectableImageContainer: {
    display: 'inline-block',
    position: 'relative',
    ...shorthands.padding('2px'),
    ...shorthands.border('2px', 'solid', tokens.colorTransparentBackground),
  },
  selectedImageContainer: {
    ...shorthands.border('2px', 'solid', tokens.colorPaletteBlueBorderActive),
    ...shorthands.borderRadius('4px'),
  },
  selectableImageList: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    ...shorthands.gap('16px'),
    ...shorthands.margin('16px', '0px', '0px'),
  },
  ratioDescription: {
    position: 'absolute',
    bottom: '8px',
    left: '8px',
  },
  imageCheckbox: {
    position: 'absolute',
    top: '4px',
    right: '4px',
  },
});

const SelectableImage = (props) => {
  const {
    id, src, description, size, onSelect, checked,
  } = props;

  const handleClick = useCallback(() => {
    onSelect(id);
  }, [id, onSelect]);

  const imageDimension = useMemo(() => {
    const dimensionList = size.split('x');
    const width = Number(dimensionList[0]);
    const height = Number(dimensionList[1]);

    // handle aspect ratio for portrait images
    if (height > width) {
      const maxHeightRatio = 500 / height;
      const maxWidthRatio = 250 / width;

      const ratio = Math.min(maxHeightRatio, maxWidthRatio);

      return {
        width: width * ratio,
        height: height * ratio,
      };
    }

    return null;
  }, [size]);

  const styles = useStyles();

  return (
    <div>
      <div
        className={mergeClasses('selectable-image-container', styles.selectableImageContainer, checked ? styles.selectedImageContainer : '')}
      >
        <Checkbox className={mergeClasses('image-checkbox', styles.imageCheckbox)} checked={checked} onChange={handleClick} />
        <div>
          <Image
            alt={description}
            bordered
            shape="rounded"
            src={src}
            fit="contain"
            onClick={handleClick}
            width={imageDimension && imageDimension.width}
            height={imageDimension && imageDimension.height}
          />
        </div>
        <Badge
          className={mergeClasses('ratio-description', styles.ratioDescription)}
          appearance="filled"
          color="important"
          shape="rounded"
          onClick={handleClick}
        >
          {description}
        </Badge>
      </div>
    </div>
  );
};

SelectableImage.propTypes = {
  id: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  size: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  checked: PropTypes.bool.isRequired,
};

const RatioPickerInternal = (props) => {
  const {
    open,
    creatives,
    onApply,
    onOpenChange,
    onApplyButtonText,
  } = props;
  const i18n = useContext(I18nContext);
  const [creativeSelections, setCreativeSelections] = useState(Array(creatives.length).fill(true));

  const [isApplying, setIsApplying] = useState(false);

  const activity = useContext(ActivityContext);

  // reset the selected creatives when the creatives change
  useEffect(() => {
    setCreativeSelections(creatives.map(() => true));
  }, [creatives]);

  const onRatioApply = useCallback(() => {
    const selectedCreatives = creatives.filter((creative, index) => creativeSelections[index]);

    setIsApplying(true);

    onApply(selectedCreatives).finally(() => {
      setIsApplying(false);
      if (activity) {
        activity.signal(`[DisplayAdsCopilot] - Successfully saved creatives - ${JSON.stringify(selectedCreatives)}`);
      }
    });
  }, [activity, creativeSelections, creatives, onApply]);

  const onImageSelect = useCallback((id) => {
    const index = creatives.findIndex(creative => creative.recommendationId === id);
    const newSelectedCreative = [...creativeSelections];
    newSelectedCreative[index] = !newSelectedCreative[index];

    setCreativeSelections(newSelectedCreative);
  }, [creatives, creativeSelections]);

  const styles = useStyles();

  return (
    <Dialog
      open={open}
      onOpenChange={(event, data) => {
        onOpenChange(data.open);
      }}
    >
      <DialogSurface>
        <div className="ratio-picker-dialog">
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button
                    appearance="subtle"
                    className="dismiss-dialog-button"
                    aria-label={i18n.getString(_TL_('Close'))}
                    icon={<Dismiss24Regular />}
                  />
                </DialogTrigger>
            }
            >
              {i18n.getString(_TL_('Which display placements do you want your creatives to serve in?'))}
            </DialogTitle>
            <DialogContent>
              {i18n.getString(_TL_('Select the checkbox next to all the placements where you want your creatives to serve. Then, we\'ll create your ads.'))}
              <div className={mergeClasses('selectable-image-list', styles.selectableImageList)}>
                {
                creatives.map((creative, index) => (
                  <SelectableImage
                    key={creative.recommendationId}
                    id={creative.recommendationId}
                    src={creative.url}
                    description={placements[creative.placement].name}
                    size={placements[creative.placement].size}
                    checked={creativeSelections[index]}
                    onSelect={onImageSelect}
                  />
                ))
              }
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" className="cancel">{i18n.getString(_TL_('Cancel'))}</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={onRatioApply}
                disabled={!creativeSelections.some(selected => selected) || isApplying}
                className="create-ads"
              >
                {
                isApplying
                  ? <Spinner size="tiny" appearance="inverted" />
                  : onApplyButtonText || i18n.getString(_TL_('Create ads'))
              }
              </Button>
            </DialogActions>
          </DialogBody>
        </div>
      </DialogSurface>
    </Dialog>
  );
};

RatioPickerInternal.propTypes = {
  open: PropTypes.bool,
  creatives: PropTypes.arrayOf(PropTypes.shape({})),
  onApply: PropTypes.func.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onApplyButtonText: PropTypes.string,
};

RatioPickerInternal.defaultProps = {
  open: false,
  creatives: [],
  onApplyButtonText: undefined,
};

const RatioPickerWithRecommendationContext = (props) => {
  const {
    onCreateAds,
    ...rest
  } = props;

  const [recommendationContext, setRecommendationContext] = useContext(RecommendationContext);
  const { logSuggestionsViewed } = useCopilotLoggers();

  const { openRatioPicker, creatives } = recommendationContext;

  const handleRatioPickerState = useCallback((state) => {
    setRecommendationContext({
      openRatioPicker: state,
    });
  }, [setRecommendationContext]);

  const onApply = useCallback(selectedCreatives => onCreateAds(selectedCreatives).then(() => handleRatioPickerState(false)), [onCreateAds, handleRatioPickerState]);

  useEffect(() => {
    if (_.isEmpty(creatives)) { return; }

    logSuggestionsViewed(creatives.map(creative => creative.recommendationId));
  }, [creatives, logSuggestionsViewed]);

  if (_.isEmpty(creatives)) {
    return null;
  }

  return (
    <RatioPickerInternal
      key={creatives.length > 0 ? creatives[0].recommendationId : 'empty'}
      creatives={creatives}
      open={openRatioPicker}
      onOpenChange={handleRatioPickerState}
      onApply={onApply}
      {...rest}
    />
  );
};

RatioPickerWithRecommendationContext.propTypes = {
  onCreateAds: PropTypes.func.isRequired,
};

export const RatioPicker = RatioPickerWithRecommendationContext;
